import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, DatePicker, InputNumber, Popconfirm, Select, Table } from 'antd';
import dayjs from 'dayjs';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip, Legend, Filler,
} from 'chart.js';
import { listStudents } from '../../api/students';
import { listResidences } from '../../api/residences';
import { listIssues } from '../../api/issues';
import { listSorties, listSortiesHoursSummary } from '../../api/sorties';
import { getCurrentSemestre } from '../../api/semestres';
import { getAbsenceStats, listSupervisorsAudit } from '../../api/supervisors';
import { approvePendingAccount, listPendingAccounts, rejectPendingAccount } from '../../api/adminPendingAccounts';
import { getApiErrorMessage } from '../../utils/apiError';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const genderToColor = (raw) => {
  const v = String(raw || '').trim().toUpperCase();
  if (!v) return null;
  if (['H', 'M', 'MALE', 'HOMME', 'MASCULIN', 'MASC'].includes(v)) return '#1677ff';
  if (['F', 'FEMALE', 'FEMME', 'FEMININ', 'FÉMININ', 'FEM'].includes(v)) return '#eb2f96';
  if (v === 'MIXTE') return '#faad14';
  return null;
};

const TABS = [
  { key: 'summary', label: 'Résumé' },
  { key: 'absence', label: 'Absences' },
  { key: 'hours-outside', label: 'Heures hors résidence' },
  { key: 'audit', label: 'Audit superviseurs' },
  { key: 'google-pending', label: 'Comptes Google en attente' },
];

export default function AdminDashboard() {
  const { notification } = App.useApp();
  const [activeTab, setActiveTab] = useState('summary');
  const [stats, setStats] = useState({ students: 0, sorties: 0, issues: 0, residences: 0 });
  const [current, setCurrent] = useState(null);
  const [residences, setResidences] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [absenceGrain, setAbsenceGrain] = useState('day');
  const [absenceGroupBy, setAbsenceGroupBy] = useState('residence');
  const [absenceRange, setAbsenceRange] = useState(() => [dayjs().subtract(29, 'day'), dayjs()]);
  const [absenceRows, setAbsenceRows] = useState([]);
  const [absenceLoading, setAbsenceLoading] = useState(false);
  const [absenceTopN, setAbsenceTopN] = useState(6);
  const [absencePage, setAbsencePage] = useState(1);
  const [absencePerPage, setAbsencePerPage] = useState(5);
  const [hoursRows, setHoursRows] = useState([]);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [hoursSearch, setHoursSearch] = useState('');
  const [hoursResidenceId, setHoursResidenceId] = useState('');
  const [hoursPage, setHoursPage] = useState(1);
  const [hoursPageSize, setHoursPageSize] = useState(20);
  const [hoursTotal, setHoursTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const cur = await getCurrentSemestre();
        const sid = cur?.id;
        const [students, res, issues, sorties] = await Promise.all([
          listStudents(sid ? { semestre_id: sid } : {}),
          listResidences(sid ? { semestre_id: sid } : {}),
          listIssues(sid ? { semestre_id: sid } : {}),
          listSorties(sid ? { page: 1, pageSize: 1, semestre_id: sid } : { page: 1, pageSize: 1 }),
        ]);
        setCurrent(cur); setResidences(Array.isArray(res) ? res : []);
        setStats({ students: students.length || 0, residences: res.length || 0, issues: issues.length || 0, sorties: sorties?.total || 0 });
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchAudit = async () => {
    setAuditLoading(true);
    try { const data = await listSupervisorsAudit({}); setAuditRows(Array.isArray(data) ? data : []); }
    catch { setAuditRows([]); }
    finally { setAuditLoading(false); }
  };

  useEffect(() => {
    fetchAudit();
    const t = setInterval(fetchAudit, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingAccounts = useCallback(async () => {
    setPendingLoading(true);
    try { const data = await listPendingAccounts(); setPendingRows(Array.isArray(data) ? data : []); }
    catch (e) { setPendingRows([]); notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les comptes.'), placement: 'topRight' }); }
    finally { setPendingLoading(false); }
  }, [notification]);

  useEffect(() => {
    fetchPendingAccounts();
    const t = setInterval(fetchPendingAccounts, 30_000);
    return () => clearInterval(t);
  }, [fetchPendingAccounts]);

  const approveAccount = useCallback(async (id) => {
    try { await approvePendingAccount(id); await fetchPendingAccounts(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Approbation échouée.'), placement: 'topRight' }); }
  }, [fetchPendingAccounts, notification]);

  const rejectAccount = useCallback(async (id) => {
    try { await rejectPendingAccount(id); await fetchPendingAccounts(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Rejet échoué.'), placement: 'topRight' }); }
  }, [fetchPendingAccounts, notification]);

  const fetchAbsence = useCallback(async () => {
    const [fromD, toD] = absenceRange || [];
    if (!fromD || !toD) return;
    setAbsenceLoading(true);
    try {
      const resp = await getAbsenceStats({ grain: absenceGrain, groupBy: absenceGroupBy, from: fromD.format('YYYY-MM-DD'), to: toD.format('YYYY-MM-DD') });
      setAbsenceRows(Array.isArray(resp?.series) ? resp.series : []);
    } catch { setAbsenceRows([]); }
    finally { setAbsenceLoading(false); }
  }, [absenceGrain, absenceGroupBy, absenceRange]);

  useEffect(() => { fetchAbsence(); }, [fetchAbsence]);
  useEffect(() => { setAbsencePage(1); }, [absenceRows, absenceGroupBy, absenceGrain, absenceRange, absencePerPage]);

  const fetchHoursOutside = useCallback(async () => {
    setHoursLoading(true);
    try {
      const resp = await listSortiesHoursSummary({
        q: hoursSearch || undefined,
        residence_id: hoursResidenceId || undefined,
        page: hoursPage,
        pageSize: hoursPageSize,
        sortBy: 'hours',
        sortDir: 'DESC',
      });
      setHoursRows(Array.isArray(resp?.items) ? resp.items : []);
      setHoursTotal(Number(resp?.total || 0));
    } catch (e) {
      setHoursRows([]);
      setHoursTotal(0);
      notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les heures hors résidence.'), placement: 'topRight' });
    } finally {
      setHoursLoading(false);
    }
  }, [hoursPage, hoursPageSize, hoursResidenceId, hoursSearch, notification]);

  useEffect(() => {
    if (activeTab !== 'hours-outside') return;
    fetchHoursOutside();
  }, [activeTab, fetchHoursOutside]);

  const residenceColors = useMemo(() => {
    const palette = ['#1677ff', '#52c41a', '#faad14', '#eb2f96', '#13c2c2', '#722ed1', '#f5222d', '#fa541c', '#2f54eb', '#a0d911'];
    const sorted = [...(residences || [])].sort((a, b) => String(a?.nom || '').localeCompare(String(b?.nom || '')));
    const m = new Map();
    const sexeColor = (sexe) => {
      const v = String(sexe || '').trim().toUpperCase();
      if (['H', 'M', 'HOMME', 'MASCULIN'].includes(v)) return '#1677ff';
      if (['F', 'FEMME', 'FILLE', 'FEMININ', 'FÉMININ'].includes(v)) return '#eb2f96';
      if (v === 'MIXTE') return '#faad14';
      return null;
    };
    for (let i = 0; i < sorted.length; i++) {
      const r = sorted[i];
      const key = String(r?.id || r?.nom || '');
      if (!key) continue;
      const c = sexeColor(r?.sexe) || palette[i % palette.length];
      m.set(key, c); if (r?.nom) m.set(String(r.nom), c);
    }
    return m;
  }, [residences]);

  const absenceChart = useMemo(() => {
    const rows = Array.isArray(absenceRows) ? absenceRows : [];
    const periods = Array.from(new Set(rows.map((r) => String(r?.period || '')))).filter(Boolean).sort();
    const globalByPeriod = new Map();
    for (const p of periods) globalByPeriod.set(p, { absent: 0, total: 0 });
    const byGroup = new Map();
    for (const r of rows) {
      const k = String(r?.group_key || ''); if (!k) continue;
      const label = String(r?.group_label || k);
      if (!byGroup.has(k)) byGroup.set(k, { key: k, label, totalSum: 0, points: new Map() });
      const g = byGroup.get(k);
      g.totalSum += Number(r?.total || 0);
      g.points.set(String(r?.period || ''), Number(r?.rate || 0));
      const p = String(r?.period || '');
      if (p && globalByPeriod.has(p)) { const agg = globalByPeriod.get(p); agg.absent += Number(r?.absent || 0); agg.total += Number(r?.total || 0); }
    }
    const groupsSorted = Array.from(byGroup.values()).sort((a, b) => (b.totalSum || 0) - (a.totalSum || 0));
    const topN = Math.max(1, Math.min(20, Number(absenceTopN || 6)));
    const groups = groupsSorted.slice(0, topN);
    const global = { key: '__global__', label: 'Global', totalSum: 0, points: new Map(periods.map((p) => { const agg = globalByPeriod.get(p) || { absent: 0, total: 0 }; return [p, agg.total > 0 ? agg.absent / agg.total : 0]; })) };
    return { periods, groups: [global, ...groups] };
  }, [absenceRows, absenceTopN]);

  const AbsenceLineChart = useMemo(() => {
    const palette = ['#1677ff', '#52c41a', '#faad14', '#eb2f96', '#13c2c2', '#722ed1'];
    return function Chart({ periods, groups, height = 260 }) {
      const labels = Array.isArray(periods) ? periods : [];
      const gs = Array.isArray(groups) ? groups : [];
      const pickColor = (g, idx) => {
        if (g?.key === '__global__') return '#262626';
        if (absenceGroupBy === 'residence') { const c = residenceColors.get(String(g?.key || '')) || residenceColors.get(String(g?.label || '')); if (c) return c; }
        if (absenceGroupBy === 'gender' || absenceGroupBy === 'sexe') { const c = genderToColor(g?.key) || genderToColor(g?.label); if (c) return c; }
        return palette[(idx - 1) % palette.length];
      };
      const datasets = gs.map((g, idx) => {
        const isGlobal = g?.key === '__global__';
        const color = pickColor(g, idx);
        return { label: String(g?.label || ''), data: labels.map((p) => { const v = g?.points?.get?.(p); return typeof v === 'number' && !Number.isNaN(v) ? v : null; }), borderColor: color, backgroundColor: color, borderWidth: 2, tension: 0.35, pointRadius: 3, pointHoverRadius: 5, spanGaps: true, fill: false, borderDash: isGlobal ? [6, 4] : undefined };
      }).filter((d) => d.label);
      const options = { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, boxHeight: 12 } }, tooltip: { callbacks: { label: (ctx) => { const v = ctx?.parsed?.y; if (typeof v !== 'number' || Number.isNaN(v)) return String(ctx?.dataset?.label || ''); return `${ctx.dataset.label}: ${Math.round(v * 1000) / 10}%`; } } } }, scales: { y: { min: 0, max: 1, ticks: { callback: (v) => `${Math.round(Number(v || 0) * 100)}%` }, grid: { color: '#f0f0f0' } }, x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 }, grid: { display: false } } } };
      return <div style={{ width: '100%', minWidth: 700, height }}><Line data={{ labels, datasets }} options={options} /></div>;
    };
  }, [absenceGroupBy, residenceColors]);

  const auditColumns = useMemo(() => [
    { title: 'Statut', width: 100, render: (_, r) => { const on = !!r?.online; return <span className={`status-dot ${on ? 'online' : 'offline'}`}>{on ? 'En ligne' : 'Hors ligne'}</span>; } },
    { title: 'Nom', render: (_, r) => `${r?.prenom || ''} ${r?.nom || ''}`.trim() || '-' },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Dernier login', width: 180, render: (_, r) => r?.last_login_at ? new Date(r.last_login_at).toLocaleString() : '-' },
    { title: 'Dernier logout', width: 180, render: (_, r) => r?.last_logout_at ? new Date(r.last_logout_at).toLocaleString() : '-' },
    { title: 'Dernier vu', width: 180, render: (_, r) => r?.last_seen_at ? new Date(r.last_seen_at).toLocaleString() : '-' },
    { title: 'IP', width: 160, dataIndex: 'ip', render: (v) => v || '-' },
    { title: 'Device', dataIndex: 'userAgent', ellipsis: true, render: (v) => v || '-' },
  ], []);

  const absencePaged = useMemo(() => {
    const base = Array.isArray(absenceRows) ? absenceRows : [];
    const total = base.length;
    const totalPages = Math.max(1, Math.ceil(total / absencePerPage));
    const safePage = Math.min(absencePage, totalPages);
    const start = (safePage - 1) * absencePerPage;
    const rows = base.slice(start, start + absencePerPage);
    const end = Math.min(start + absencePerPage, total);
    return { rows, total, totalPages, safePage, start, end };
  }, [absencePage, absencePerPage, absenceRows]);

  return (
    <div className="page">
      <div className="section-label">Tableau de bord — Administrateur</div>
      {current && <p style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Semestre actif: <strong>{current.nom} {current.annee}</strong></p>}

      {/* Custom Tab Bar */}
      <div className="tab-bar">
        {TABS.map((t) => (
          <button key={t.key} className={`tab-btn${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* — Tab: Résumé — */}
      {activeTab === 'summary' && (
        <div className="stats-grid">
          {[
            { label: 'Étudiants', value: stats.students },
            { label: 'Sorties (total)', value: stats.sorties },
            { label: 'Signalements', value: stats.issues },
            { label: 'Résidences', value: stats.residences },
          ].map((s) => (
            <div className="stat-card" key={s.label}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* — Tab: Absences — */}
      {activeTab === 'absence' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ color: '#888', fontSize: 12, margin: 0 }}>Taux d'absence estimé via sorties en cours (date_sortie sans date_retour_effective) sur la période.</p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Select style={{ width: 160 }} value={absenceGrain} onChange={setAbsenceGrain} options={[{ value: 'day', label: 'Par jour' }, { value: 'week', label: 'Par semaine' }, { value: 'month', label: 'Par mois' }]} />
              <Select style={{ width: 210 }} value={absenceGroupBy} onChange={setAbsenceGroupBy} options={[{ value: 'residence', label: 'Comparer: Résidences' }, { value: 'sexe', label: 'Comparer: Sexe résidence' }, { value: 'batiment', label: 'Comparer: Bâtiment' }, { value: 'gender', label: 'Comparer: Genre' }, { value: 'promotion', label: 'Comparer: Promotion' }, { value: 'niveau', label: 'Comparer: Niveau' }]} />
              <DatePicker.RangePicker value={absenceRange} onChange={(v) => setAbsenceRange(v || [])} format="YYYY-MM-DD" allowClear={false} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: '#888' }}>Top</span>
                <InputNumber min={1} max={20} value={absenceTopN} onChange={(v) => setAbsenceTopN(Number(v || 1))} style={{ width: 70 }} />
              </div>
            </div>
            <span style={{ fontSize: 11, color: '#aaa' }}>{absenceLoading ? 'Chargement…' : `${absenceRows.length} lignes`}</span>
          </div>

          <div className="card">
            <div className="card-inner">
              <div className="section-label" style={{ marginBottom: 8 }}>Courbe (taux d'absence %)</div>
              {absenceChart.periods.length <= 1 || absenceChart.groups.length === 0
                ? <p className="table-empty" style={{ padding: '16px 0' }}>Pas assez de données pour afficher une courbe.</p>
                : <AbsenceLineChart periods={absenceChart.periods} groups={absenceChart.groups} />}
            </div>
          </div>


          <div className="table-card admin-absence-table">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Période</th>
                    <th>Groupe</th>
                    <th className="col-right">Absents</th>
                    <th className="col-right">Total</th>
                    <th className="col-right">Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {absenceLoading ? (
                    <tr><td colSpan={5} className="table-loading">Chargement...</td></tr>
                  ) : absencePaged.total === 0 ? (
                    <tr><td colSpan={5} className="table-empty">Aucune donnée</td></tr>
                  ) : (
                    absencePaged.rows.map((r) => {
                      const label = String(r?.group_label || r?.group_key || '-');
                      let color = '#4aacef';
                      if (absenceGroupBy === 'residence') color = residenceColors.get(String(r?.group_key || '')) || residenceColors.get(label) || '#4aacef';
                      else if (absenceGroupBy === 'gender' || absenceGroupBy === 'sexe') color = genderToColor(r?.group_key) || genderToColor(label) || '#4aacef';
                      return (
                        <tr key={`${r.period}|${r.group_key}`}>
                          <td>{r?.period || '-'}</td>
                          <td>
                            <span className="groupe-cell">
                              <span className="dot" style={{ background: color }} />
                              <span>{label}</span>
                            </span>
                          </td>
                          <td className="col-right">{Number(r?.absent || 0)}</td>
                          <td className="col-right">{Number(r?.total || 0)}</td>
                          <td className="col-right">{`${Math.round(Number(r?.rate || 0) * 1000) / 10}%`}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {!absenceLoading && absencePaged.total > 0 && (
              <div className="pagination-bar">
                <span className="pag-info">{`${absencePaged.start + 1}-${absencePaged.end} sur ${absencePaged.total}`}<br />lignes</span>
                <button className="pag-btn" onClick={() => setAbsencePage((p) => Math.max(1, p - 1))} disabled={absencePaged.safePage <= 1}>&#8249;</button>
                <button className="pag-btn active">{absencePaged.safePage}</button>
                <button className="pag-btn" onClick={() => setAbsencePage((p) => Math.min(absencePaged.totalPages, p + 1))} disabled={absencePaged.safePage >= absencePaged.totalPages}>&#8250;</button>
                <select className="pag-select" value={String(absencePerPage)} onChange={(e) => setAbsencePerPage(Number(e.target.value))}>
                  <option value="5">5 / page</option>
                  <option value="10">10 / page</option>
                  <option value="20">20 / page</option>
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* — Tab: Audit — */}
      {activeTab === 'hours-outside' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ color: '#888', fontSize: 12, margin: 0 }}>
            Total d&apos;heures passées hors résidence par étudiant sur le semestre actif (sorties avec départ réel).
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="search-input"
              style={{ minWidth: 280, flex: 1 }}
              placeholder="Rechercher un étudiant (ID, nom, prénom, email)"
              value={hoursSearch}
              onChange={(e) => { setHoursSearch(e.target.value); setHoursPage(1); }}
            />
            <Select
              style={{ width: 230 }}
              value={hoursResidenceId}
              onChange={(v) => { setHoursResidenceId(v); setHoursPage(1); }}
              options={[
                { value: '', label: 'Toutes les résidences' },
                ...(residences || []).map((r) => ({ value: r.id, label: r.nom })),
              ]}
            />
            <Select
              style={{ width: 120 }}
              value={hoursPageSize}
              onChange={(v) => { setHoursPageSize(Number(v)); setHoursPage(1); }}
              options={[{ value: 10, label: '10 / page' }, { value: 20, label: '20 / page' }, { value: 50, label: '50 / page' }]}
            />
          </div>
          <Table
            className="admin-dashboard-table"
            rowKey="student_id"
            loading={hoursLoading}
            dataSource={hoursRows}
            columns={[
              { title: 'ID étudiant', dataIndex: 'student_code', width: 130 },
              { title: 'Étudiant', dataIndex: 'student_name' },
              { title: 'Email', dataIndex: 'student_email' },
              { title: 'Résidence', dataIndex: 'residence', width: 180 },
              { title: 'Sorties comptées', dataIndex: 'sorties_count', width: 140 },
              { title: 'Total heures hors résidence', dataIndex: 'total_hours_outside', width: 220, render: (v) => `${Number(v || 0).toFixed(2)} h` },
            ]}
            pagination={{
              current: hoursPage,
              pageSize: hoursPageSize,
              total: hoursTotal,
              onChange: (p, ps) => {
                setHoursPage(p);
                if (ps && ps !== hoursPageSize) setHoursPageSize(ps);
              },
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}–${range[1]} sur ${total} étudiants`,
            }}
          />
        </div>
      )}

      {/* — Tab: Audit — */}
      {activeTab === 'audit' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ color: '#888', fontSize: 12, margin: 0 }}>Statut "En ligne" estimé via activité récente (heartbeat).</p>
          <Table className="admin-dashboard-table" rowKey="id" loading={auditLoading} columns={auditColumns} dataSource={auditRows} pagination={{ hideOnSinglePage: true, pageSize: 20 }} />
        </div>
      )}

      {/* — Tab: Google pending — */}
      {activeTab === 'google-pending' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ color: '#888', fontSize: 12, margin: 0 }}>Comptes créés via Google OAuth en statut <code>pending</code>.</p>
          <Table
            className="admin-dashboard-table"
            rowKey="id"
            loading={pendingLoading}
            pagination={{ hideOnSinglePage: true, pageSize: 20 }}
            sticky={{ offsetHeader: 64 }}
            dataSource={pendingRows}
            columns={[
              { title: 'Email', dataIndex: 'email', width: 260 },
              { title: 'Nom complet', width: 240, render: (_, r) => `${String(r?.prenom || '').trim()} ${String(r?.nom || '').trim()}`.trim() || '-' },
              { title: 'Rôle', dataIndex: 'role', width: 140 },
              { title: 'Statut', dataIndex: 'status', width: 130 },
              { title: 'Google ID', dataIndex: 'google_id', ellipsis: true, render: (v) => v ? String(v) : '-' },
              { title: 'Créé', width: 170, render: (_, r) => r?.createdAt ? new Date(r.createdAt).toLocaleString() : '-' },
              {
                title: 'Actions', width: 200,
                render: (_, r) => (
                  <div className="actions-cell">
                    <Popconfirm title="Approuver ce compte ?" okText="Approuver" cancelText="Annuler" onConfirm={() => approveAccount(r.id)}>
                      <button className="btn-primary" style={{ fontSize: 11, height: 28, padding: '0 10px' }}>Approuver</button>
                    </Popconfirm>
                    <Popconfirm title="Rejeter ce compte ?" okText="Rejeter" cancelText="Annuler" onConfirm={() => rejectAccount(r.id)}>
                      <button className="btn-outline" style={{ fontSize: 11, height: 28, padding: '0 10px', color: '#e24b4a', borderColor: 'rgba(226,75,74,0.3)' }}>Rejeter</button>
                    </Popconfirm>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

